import { useEffect, useState } from 'react';
import { get, put } from 'helpers/apiHelpers';
import { fetchDiets } from 'actions/Diets';
import { fetchMealTypes } from 'actions/MealTypes';
import { fetchVariants } from 'actions/Variants';
import { useDispatch, useSelector } from 'react-redux';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { Divider } from '@material-ui/core';
import 'views/Orders/calendar.css';
import { DialogLoader } from 'components/DialogLoader';
import LogView from 'components/History/LogView';
import { useTranslation } from 'react-i18next';
import dates from 'react-big-calendar/lib/utils/dates';
import moment from 'moment';
import BigCalendar from 'react-big-calendar';
import { makeStyles } from '@material-ui/styles';
import { useHistory } from 'react-router';

import SingleDayDetails from './SingleDayDetails';
import Calendar from './Calendar/Calendar';
import CreatedBy from './CreatedBy';
import DietDetailsHeader from './DietDetailsHeader/DietDetailsHeader';
import DietDetailLeftSection from './DietDetailLeftSection/DietDetailLeftSection';

const useStyles = makeStyles({
  ...extendedFormsStyle,
  ...buttonsStyle,
  mainCard: {
    minWidth: '1280px',
    marginBottom: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
  },
  mainCardWrapper: { overflowX: 'scroll', marginBottom: '30px' },
  newOrderWrapper: { marginLeft: '5px', marginBottom: '10px' },
  divider: { margin: '0 24px 24px' },
});

const localizer = BigCalendar.momentLocalizer(moment);

const DietDetails = ({ dietId, setDietId }) => {
  const [pickupPoints, setPickupPoints] = useState([]);
  const [addressType, setAddressType] = useState('');
  const [bagInfo, setBagInfo] = useState(null);
  const [selectedMealTypes, setSelectedMealTypes] = useState([]);
  const [brandInfo, setBrandInfo] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [isCreatedByEmployee, setIsCreatedByEmployee] = useState(false);

  const [selectedDiet, selectDiet] = useState('');

  const [client, setClient] = useState({});
  const [dietInfo, setDietInfo] = useState(null);

  const [allDiets, setAllDiets] = useState([]);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchingDay, setFetchingDay] = useState(false);
  const [firstVisibleDay, setFirstVisibleDay] = useState('');
  const [lastVisibleDay, setLastVisibleDay] = useState('');
  const [isSelectedDaySubscriptionIntent, setIsSelectedDaySubscription] =
    useState(false);

  const [historyOrder, setHistoryOrder] = useState({});

  const [calendarNeedsUpdate, setCalendarNeedsUpdate] = useState(false);
  const { t } = useTranslation();

  const history = useHistory();
  const dispatch = useDispatch();

  const classes = useStyles();

  const { diets } = useSelector(({ Diets }) => Diets);
  const { variants } = useSelector(({ Variants }) => Variants);
  const { mealTypes } = useSelector(({ MealTypes }) => MealTypes);

  const getEcommerceDietsParams = clientId => ({
    pagination: false,
    'client.id': clientId,
    properties: [
      'id',
      'name',
      'active',
      'firstDeliveryDate',
      'lastDeliveryDate',
    ],
    'properties[diet]': ['name'],
  });

  const fetchOnDietChange = ecommerceDietId => {
    setIsLoading(true);
    get(`/ecommerce-diets/${ecommerceDietId}`)
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(ecommerceDiet => {
        const clientId = ecommerceDiet.client['@id'];

        // eslint-disable-next-line new-cap -- FIXME
        const firstDeliveryDate = new moment(
          dates.firstVisibleDay(
            new Date(ecommerceDiet.firstDeliveryDate),
            localizer
          )
        ).format('YYYY-MM-DD');

        // eslint-disable-next-line new-cap -- FIXME
        const lastDeliveryDate = new moment(
          dates.lastVisibleDay(
            new Date(ecommerceDiet.lastDeliveryDate),
            localizer
          )
        ).format('YYYY-MM-DD');

        let pureClientId = clientId.match(/\d/g);
        pureClientId = pureClientId.join('');

        setDietInfo(ecommerceDiet);
        setFirstVisibleDay(firstDeliveryDate);
        setLastVisibleDay(lastDeliveryDate);

        return Promise.all([
          get(`${ecommerceDiet.client['@id']}/addresses`),
          get(clientId),
          get('/notes', { key: clientId }),
          get('ecommerce-diets', getEcommerceDietsParams(pureClientId)),
          get(ecommerceDiet.order['@id']),
          get(ecommerceDiet.order['@id'], {
            showHistory: true,
            actionType: 'INSERT',
            'order.id': 'ASC',
          }),
        ]);
      })
      // eslint-disable-next-line promise/prefer-await-to-then, no-shadow -- FIXME
      .then(([addresses, client, notes, allDiets, orderInfo, history]) => {
        setCalendarNeedsUpdate(true);
        setNotes(notes['hydra:member']);
        setClient(client);
        setClientAddresses(addresses['hydra:member']);
        setOrderInfo(orderInfo);
        setAllDiets(allDiets['hydra:member']);

        const historyCreateOrder = history['hydra:member'][0];

        setIsCreatedByEmployee(
          historyCreateOrder?.impersonator
            ? historyCreateOrder?.impersonator?.['@type'] === 'Employee'
            : historyCreateOrder?.actor?.['@type'] === 'Employee'
        );
        setHistoryOrder(historyCreateOrder);
        setIsLoading(false);
      });
  };

  const handleSaveCustomDietName = dietName => {
    put(`/ecommerce-diets/${dietInfo.id}`, { name: dietName })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(res => {
        setDietInfo(res);

        window.location.reload();
      })
      // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
      .catch(err => {
        // eslint-disable-next-line no-console -- FIXME
        console.error(err);
      });
  };

  const fetchData = () => {
    Promise.all([
      get(`/ecommerce-diets/${dietId}`),
      get('/pick-up-points'),
      dispatch(fetchDiets()),
      dispatch(fetchVariants()),
      dispatch(fetchMealTypes()),
    ])
      // eslint-disable-next-line promise/prefer-await-to-then, no-shadow -- FIXME
      .then(([ecommerceDiet, pickupPoints]) => {
        const clientId = ecommerceDiet.client['@id'];
        let pureClientId = clientId.match(/\d/g);
        pureClientId = pureClientId.join('');

        // eslint-disable-next-line new-cap -- FIXME
        const firstDeliveryDate = new moment(
          dates.firstVisibleDay(
            new Date(ecommerceDiet.firstDeliveryDate),
            localizer
          )
        ).format('YYYY-MM-DD');

        // eslint-disable-next-line new-cap -- FIXME
        const lastDeliveryDate = new moment(
          dates.lastVisibleDay(
            new Date(ecommerceDiet.lastDeliveryDate),
            localizer
          )
        ).format('YYYY-MM-DD');

        setPickupPoints(pickupPoints['hydra:member']);
        setDietInfo(ecommerceDiet);
        setFirstVisibleDay(firstDeliveryDate);
        setLastVisibleDay(lastDeliveryDate);

        return Promise.all([
          get(`${ecommerceDiet.client['@id']}/addresses`),
          get(clientId),
          get('/notes', { key: clientId }),
          get('ecommerce-diets', getEcommerceDietsParams(pureClientId)),
          get(ecommerceDiet.order['@id']),
          get(ecommerceDiet.order['@id'], {
            showHistory: true,
            actionType: 'INSERT',
            'order[id]': 'ASC',
          }),
        ]);
      })
      // eslint-disable-next-line promise/prefer-await-to-then, no-shadow -- FIXME
      .then(([addresses, client, notes, allDiets, orderInfo, history]) => {
        setNotes(notes['hydra:member']);
        setClient(client);
        setClientAddresses(addresses['hydra:member']);

        setOrderInfo(orderInfo);
        setAllDiets(allDiets['hydra:member']);

        const historyCreateOrder = history['hydra:member'][0];
        setIsCreatedByEmployee(
          historyCreateOrder?.impersonator
            ? historyCreateOrder?.impersonator?.['@type'] === 'Employee'
            : historyCreateOrder?.actor?.['@type'] === 'Employee'
        );

        setHistoryOrder(historyCreateOrder);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  if (isLoading) {
    const pathnameAsArray = window.location.pathname.split('/');
    if (pathnameAsArray.includes('edit')) {
      return null;
    }

    return <DialogLoader loaderState={isLoading} text={t('common.loader')} />;
  }

  const newOrder = () => {
    history.push(`/admin/orders/add?userId=${client.id}`);
  };

  const createdDate =
    historyOrder &&
    moment(historyOrder.loggedAt).format(
      moment.localeData().longDateFormat('L')
    );
  const createdTime =
    historyOrder && moment(historyOrder.loggedAt).format('HH:mm:ss');

  const removeAddons = addonId => {
    const dietSubscriptionId = dietInfo?.subscription?.id;

    const url = isSelectedDaySubscriptionIntent
      ? `subscription/${dietSubscriptionId}/remove-addons`
      : `${bagInfo['@id']}/remove-addons`;

    put(url, {
      addons: [addonId],
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    }).then(setBagInfo);
  };

  return (
    <div>
      <CreatedBy
        isCreatedByEmployee={isCreatedByEmployee}
        dietCreator={historyOrder}
        createdDate={createdDate}
        createdTime={createdTime}
      />
      <DietDetailsHeader
        dietInfo={dietInfo}
        client={client}
        newOrder={newOrder}
        notes={notes}
        setNotes={setNotes}
        setClient={setClient}
      />
      <div className={classes.mainCardWrapper}>
        <Card className={classes.mainCard}>
          <Divider className={classes.divider} />
          <CardBody>
            <GridContainer>
              <GridItem md={3}>
                <DietDetailLeftSection
                  setDietId={setDietId}
                  dietInfo={dietInfo}
                  orderInfo={orderInfo}
                  allDiets={allDiets}
                  handleSave={handleSaveCustomDietName}
                  fetchOnDietChange={fetchOnDietChange}
                />
              </GridItem>
              <GridItem md={6}>
                <Calendar
                  localizer={localizer}
                  dietId={dietId}
                  dietInfo={dietInfo}
                  bagInfo={bagInfo}
                  setBagInfo={setBagInfo}
                  setAddressType={setAddressType}
                  setFetchingDay={setFetchingDay}
                  fetchingDay={fetchingDay}
                  diets={diets}
                  selectDiet={selectDiet}
                  firstVisibleDay={firstVisibleDay}
                  setFirstVisibleDay={setFirstVisibleDay}
                  lastVisibleDay={lastVisibleDay}
                  setLastVisibleDay={setLastVisibleDay}
                  calendarNeedsUpdate={calendarNeedsUpdate}
                  setCalendarNeedsUpdate={setCalendarNeedsUpdate}
                  setBrandInfo={setBrandInfo}
                  setIsSelectedDaySubscription={setIsSelectedDaySubscription}
                  calendarInitDate={dietInfo.calendarInitDate}
                />
              </GridItem>
              <GridItem md={3}>
                <SingleDayDetails
                  classes={classes}
                  dietId={dietId}
                  bagInfo={bagInfo}
                  dietInfo={dietInfo}
                  setBagInfo={setBagInfo}
                  variants={variants}
                  diets={diets}
                  mealTypes={mealTypes}
                  fetchOnDietChange={fetchOnDietChange}
                  setAddressType={setAddressType}
                  addressType={addressType}
                  clientAddresses={clientAddresses}
                  pickupPoints={pickupPoints}
                  fetchingDay={fetchingDay}
                  setFetchingDay={setFetchingDay}
                  selectedDiet={selectedDiet}
                  selectDiet={selectDiet}
                  calendarNeedsUpdate={calendarNeedsUpdate}
                  setCalendarNeedsUpdate={setCalendarNeedsUpdate}
                  brandInfo={brandInfo}
                  orderInfo={orderInfo}
                  removeAddons={removeAddons}
                  isSelectedDaySubscriptionIntent={
                    isSelectedDaySubscriptionIntent
                  }
                  selectedMealTypes={selectedMealTypes}
                  setSelectedMealTypes={setSelectedMealTypes}
                />
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </div>
      <LogView iri={`/ecommerce-diets/${dietId}`} />
    </div>
  );
};

export default DietDetails;
